import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { billingApi } from '@/lib/api/endpoints';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import { formatAmount, formatMinutes } from '@/routes/Clients';

/**
 * The printed protocol — a page whose real reader holds paper.
 *
 * Rendered outside the app shell: a work report carries no sidebar, and
 * Cmd+P must produce a clean A4, not a screenshot of an app. The lines come
 * frozen from the server; nothing here recomputes money.
 */
export function ReportPrintRoute() {
  const t = useT();
  const { reportId = '' } = useParams();
  const { user } = useAuth();
  const { data: report } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => billingApi.report(reportId),
    enabled: Boolean(reportId),
  });
  const { data: clients } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => billingApi.clients(true),
  });
  const client = clients?.find((row) => row.id === report?.client_id);

  if (!report) return null;

  return (
    <div className="mx-auto max-w-2xl bg-white p-8 text-neutral-900 print:max-w-none print:p-0">
      <div className="mb-6 flex items-center justify-between gap-2 print:hidden">
        <Link
          to={`/clients/${report.client_id}`}
          className="flex items-center gap-1 text-sm text-accent hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t('report.back')}
        </Link>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t('report.print')}
        </Button>
      </div>

      <header className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">{t('report.title')}</h1>
        <p className="mt-1 text-sm text-neutral-500">
          {report.number} · {formatDay(report.period_start)} – {formatDay(report.period_end)}
        </p>
      </header>

      <div className="mb-8 grid grid-cols-2 gap-8 text-sm">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-neutral-400">
            {t('report.supplier')}
          </p>
          <p className="font-medium">{user?.display_name}</p>
          <p className="text-neutral-600">{user?.email}</p>
        </div>
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-neutral-400">
            {t('report.client')}
          </p>
          <p className="font-medium">{client?.company || client?.name}</p>
          {client?.registration_no ? (
            <p className="text-neutral-600">IČO: {client.registration_no}</p>
          ) : null}
          {client?.address ? (
            <p className="whitespace-pre-line text-neutral-600">{client.address}</p>
          ) : null}
        </div>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-neutral-900 text-left">
            <th className="py-2 pr-2 font-semibold">{t('report.date')}</th>
            <th className="py-2 pr-2 font-semibold">{t('report.work')}</th>
            <th className="py-2 pr-2 text-right font-semibold">{t('report.hours')}</th>
            <th className="py-2 pr-2 text-right font-semibold">{t('report.rate')}</th>
            <th className="py-2 text-right font-semibold">{t('report.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {report.lines.map((line, index) => (
            <tr key={index} className="border-b border-neutral-200 align-top">
              <td className="whitespace-nowrap py-2 pr-2 tabular-nums">
                {formatDay(line.date)}
              </td>
              <td className="py-2 pr-2">
                {line.task}
                {line.note ? (
                  <span className="block text-xs text-neutral-500">{line.note}</span>
                ) : null}
              </td>
              {/* A fixed price has no hourly arithmetic behind it: printing
                  "0:00" and "0 Kč/h" beside it would invite the question of
                  which number is wrong. */}
              <td className="whitespace-nowrap py-2 pr-2 text-right tabular-nums">
                {line.kind === 'fixed' ? '—' : formatMinutes(line.minutes)}
              </td>
              <td className="whitespace-nowrap py-2 pr-2 text-right tabular-nums">
                {line.kind === 'fixed' || line.rate === null
                  ? '—'
                  : formatAmount(Number(line.rate), report.currency)}
              </td>
              <td className="whitespace-nowrap py-2 text-right tabular-nums">
                {formatAmount(Number(line.amount), report.currency)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-neutral-900 font-semibold">
            <td className="py-3" colSpan={2}>
              {t('report.total')}
            </td>
            <td className="py-3 text-right tabular-nums">
              {formatMinutes(report.total_minutes)}
            </td>
            <td />
            <td className="py-3 text-right tabular-nums">
              {formatAmount(Number(report.total_amount), report.currency)}
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="mt-10 text-xs text-neutral-400">
        {t('report.issued')} {formatDay(report.created_at.slice(0, 10))} · MirOnDo
      </p>
    </div>
  );
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day}. ${month}. ${year}`;
}
